import React from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Button } from '@oysterjs/ui/button';
import { Field, Label } from '@oysterjs/ui/fieldset';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';

import { useBulkDraftClientSearchQuery } from '~/api/graphql';
import { useDebouncedValue } from '~/hooks/use-list-controls';
import type { BulkDraftRecipient } from './bulk-draft-recipient-status';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_SEARCH_RESULTS = 10;
/** The search answers nothing until a word is this long. */
const MIN_SEARCH_WORD_LENGTH = 3;

const isSearchable = (query: string) =>
  query.split(/\s+/).some((word) => word.length >= MIN_SEARCH_WORD_LENGTH);

const addressKey = (email: string | null | undefined) => email?.trim().toLowerCase() ?? '';

/** Searches the agency's AMS accounts and adds a picked one to the bulk draft. */
export const BulkDraftClientSearch = ({
  recipients,
  isFull,
  query,
  onQueryChange,
  onAdd
}: {
  /** The clients already listed, which show as Added. */
  recipients: BulkDraftRecipient[];
  isFull: boolean;
  /** What's typed in the search box, kept by the page so Add row can start from it. */
  query: string;
  onQueryChange: (query: string) => void;
  onAdd: (client: { email: string; name?: string; amsAccountId: string }) => Promise<void>;
}) => {
  const [addingAccountId, setAddingAccountId] = React.useState<string>();
  const searchedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(searchedQuery, SEARCH_DEBOUNCE_MS);
  const { data, loading, error } = useBulkDraftClientSearchQuery({
    variables: { query: debouncedQuery, limit: MAX_SEARCH_RESULTS },
    skip: !isSearchable(debouncedQuery)
  });
  const accounts = (data?.searchRecipients ?? []).filter((result) => !!result.amsAccountId);
  const addedAccountIds = new Set(recipients.map((recipient) => recipient.amsAccountId));
  const listedAddresses = new Set(recipients.map((recipient) => addressKey(recipient.email)));
  // Checks what's typed as well as the debounced query, so clearing the box (its X) hides the
  // results at once, and a search still in flight can't bring them back.
  const isShowingResults = isSearchable(searchedQuery) && isSearchable(debouncedQuery);

  const add = async (account: (typeof accounts)[number]) => {
    const accountId = account.amsAccountId ?? '';
    setAddingAccountId(accountId);
    await onAdd({ email: account.email, name: account.name ?? undefined, amsAccountId: accountId });
    setAddingAccountId(undefined);
  };

  return (
    <div className="w-full min-w-0 space-y-2 sm:min-w-64 sm:flex-1">
      <Field disabled={isFull}>
        <Label>Add another client</Label>
        <InputGroup>
          <MagnifyingGlassIcon data-slot="icon" />
          <Input
            type="search"
            className="[&_input]:text-ellipsis"
            placeholder="Search your AMS for clients by name, company or email"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
          />
        </InputGroup>
      </Field>

      {!isShowingResults ? null : loading ? (
        <span
          role="status"
          className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400"
        >
          <Spinner size="size-4" color="neutral" />
          Searching your AMS accounts…
        </span>
      ) : error ? (
        <span role="alert" className="block text-sm text-red-600 dark:text-red-400">
          We couldn&apos;t search your AMS accounts just now. Try again, or add them with Add row.
        </span>
      ) : accounts.length === 0 ? (
        <div className="text-sm">
          <span className="block font-medium text-neutral-900 dark:text-white">
            No AMS accounts match &quot;{debouncedQuery}&quot;.
          </span>
          <span className="mt-1 block text-neutral-500 dark:text-neutral-400">
            Try their name, company or email address. An account added to your AMS in the last few
            minutes may not be in Cara yet. You can add anyone with Add row.
          </span>
        </div>
      ) : (
        <ul className="max-h-60 divide-y divide-neutral-200 overflow-y-auto rounded-lg border border-neutral-200 bg-white dark:divide-neutral-700 dark:border-neutral-700 dark:bg-neutral-800">
          {accounts.map((account) => {
            const accountId = account.amsAccountId ?? '';
            const isAdded =
              addedAccountIds.has(accountId) || listedAddresses.has(addressKey(account.email));
            const accountName = account.name ?? account.email;
            return (
              <li key={accountId} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0 text-sm">
                  <span
                    className="block truncate font-medium text-neutral-900 dark:text-white"
                    title={accountName}
                  >
                    {accountName}
                  </span>
                  <span className="block truncate text-neutral-500 dark:text-neutral-400">
                    {[account.description, account.email].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <Button
                  color="white"
                  className="shrink-0"
                  title={isAdded ? 'Already in the list' : undefined}
                  disabled={isAdded || isFull || !!addingAccountId}
                  loading={addingAccountId === accountId}
                  onClick={() => add(account)}
                >
                  {isAdded ? 'Added' : 'Add'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
